import { ITeam, TeamLevel } from "@/interfaces/teams/teams";
import request from "@/utils/request";
import POSConfigModule from "@/store/modules/pos-config";
import { withNotSyncedRetry } from "@/utils/helpers/not-synced-retry";

// Order-keyed Teams (pairing) endpoints of the main API (ORD-770). They resolve
// the legacy booking from the NOL order id server-side and run the same service
// logic as the booking-keyed routes, with the same response shape: business
// errors such as a duplicate team name come back as 200 with `status: "fail"`.
// All of them answer 409 NOT_SYNCED until the completed order reaches the
// legacy booking table, retried by `withNotSyncedRetry`.

/** Team as serialized by the main API (`team_of_participants` group). */
interface ITeamResponse {
  id: number;
  uuid: string;
  name: string | null;
  nfcTag: string | null;
  startDateTimeTz: string | null;
  endDateTimeTz: string | null;
  teamLevel: number;
  roomPlayers: number;
  /** Absent on backends older than ORD-770, which only know active teams. */
  cancelled?: boolean;
}

interface ITeamActionResponse {
  status: "success" | "fail";
  data: { teamOfParticipants?: ITeamResponse; errors?: string[] };
}

function toTeam(team: ITeamResponse): ITeam {
  return {
    uuid: team.uuid,
    name: team.name ?? "",
    roomPlayers: Number(team.roomPlayers),
    startDateTime: team.startDateTimeTz ?? "",
    endDateTime: team.endDateTimeTz ?? "",
    nfcTag: team.nfcTag ?? "",
    paired: true,
    tokens: [],
    teamLevel: team.teamLevel || TeamLevel.MEDIUM,
  };
}

/**
 * Active teams of a NOL order.
 * `GET /order/:orderId/teams?cancelled=0`
 *
 * Retries while the order is not synced yet (409 NOT_SYNCED), then throws the
 * last error. The `cancelled` filter is ignored by backends older than
 * ORD-770, so the flag is checked again on the payload.
 *
 * @param orderId Order id
 */
export async function getOrderTeams(orderId: string): Promise<ITeam[]> {
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/order/${orderId}/teams`,
      method: "get",
      params: { cancelled: 0 },
    })
  );
  const teams: ITeamResponse[] = response.data?.data?.teams ?? [];
  return teams.filter((team) => !team.cancelled).map(toTeam);
}

/**
 * Pair (create) a team on a NOL order.
 * `POST /group/:groupId/order/:orderId/team`
 *
 * @param team Team to create; its dates are venue wall clock
 * @param orderId Order id
 * @returns the backend envelope: `status: "fail"` with `data.errors` for a
 * business refusal such as a duplicate team name
 */
export async function postPairTeam(
  team: ITeam,
  orderId: string
): Promise<ITeamActionResponse> {
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/group/${POSConfigModule.groupId}/order/${orderId}/team`,
      method: "post",
      data: {
        name: team.name,
        startDateTimeTz: team.startDateTime,
        endDateTimeTz: team.endDateTime,
        roomPlayers: team.roomPlayers,
        nfcTag: team.nfcTag,
        teamLevel: team.teamLevel,
      },
    })
  );
  return response.data;
}

/**
 * Update a paired team (name, NFC tag, level, players).
 * `PATCH /group/:groupId/order/:orderId/team/:uuid`
 *
 * @param team Team to update, by its uuid
 * @param orderId Order id
 */
export async function patchPairTeam(
  team: ITeam,
  orderId: string
): Promise<ITeamActionResponse> {
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/group/${POSConfigModule.groupId}/order/${orderId}/team/${team.uuid}`,
      method: "patch",
      data: {
        name: team.name,
        roomPlayers: team.roomPlayers,
        nfcTag: team.nfcTag,
        teamLevel: team.teamLevel,
      },
    })
  );
  return response.data;
}

/**
 * Cancel a team of a NOL order.
 * `DELETE /group/:groupId/order/:orderId/team/:uuid`
 *
 * @param teamUuid Team uuid
 * @param orderId Order id
 */
export async function deleteTeam(
  teamUuid: string,
  orderId: string
): Promise<ITeamActionResponse> {
  const response = await withNotSyncedRetry(() =>
    request({
      url: `/group/${POSConfigModule.groupId}/order/${orderId}/team/${teamUuid}`,
      method: "delete",
    })
  );
  return response.data;
}
